import { Button, Input, Modal } from '@components/common';
import { DropdownSelect } from '@components/common/DropdownSelect';
import { AlertTriangle } from 'lucide-react';
import { buildItemCd, isValidItemCd, parseItemCd } from 'ebm-rw';
import React, { useEffect, useMemo, useState } from 'react';
import {
  EBM_DEFAULT_ORIGIN_NATION_CD,
  EBM_PACKAGING_UNIT_CODES,
  EBM_PRODUCT_TYPE_OPTIONS,
  EBM_QUANTITY_UNIT_CODES,
} from '@/renderer/constants/ebmUnitCodes';
import { fetchNextEbmItemSequence } from '@/renderer/services/menuService';

interface ItemCodeBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (itemCd: string) => void;
  outletId: string;
  initialItemCd?: string | null;
  itemName?: string;
}

const PACKAGING_OPTIONS = EBM_PACKAGING_UNIT_CODES.filter(
  (c) => c.selectable,
).map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }));

const QUANTITY_OPTIONS = EBM_QUANTITY_UNIT_CODES.filter(
  (c) => c.selectable,
).map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }));

export const ItemCodeBuilderModal: React.FC<ItemCodeBuilderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  outletId,
  initialItemCd,
  itemName,
}) => {
  const [origin, setOrigin] = useState(EBM_DEFAULT_ORIGIN_NATION_CD);
  const [productType, setProductType] = useState('2');
  const [pkgUnit, setPkgUnit] = useState('');
  const [qtyUnit, setQtyUnit] = useState('');
  const [sequence, setSequence] = useState('');
  const [sequenceTouched, setSequenceTouched] = useState(false);
  const [isSuggestingSequence, setIsSuggestingSequence] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const trimmed = initialItemCd?.trim() ?? '';
    if (trimmed && isValidItemCd(trimmed)) {
      const parts = parseItemCd(trimmed);
      setOrigin(parts.originNationCd);
      setProductType(parts.productTypeCd);
      setPkgUnit(parts.pkgUnitCd);
      setQtyUnit(parts.qtyUnitCd);
      setSequence(String(parts.sequence));
      setSequenceTouched(true);
    } else {
      setOrigin(EBM_DEFAULT_ORIGIN_NATION_CD);
      setProductType('2');
      setPkgUnit('');
      setQtyUnit('');
      setSequence('');
      setSequenceTouched(false);
    }
  }, [isOpen, initialItemCd]);

  useEffect(() => {
    if (!isOpen || sequenceTouched) return undefined;
    if (!/^[A-Z]{2}$/.test(origin) || !productType || !pkgUnit || !qtyUnit) {
      return undefined;
    }
    let cancelled = false;
    setIsSuggestingSequence(true);
    const suggestSequence = async () => {
      try {
        const next = await fetchNextEbmItemSequence(outletId, {
          originNationCd: origin,
          productTypeCd: productType,
          pkgUnitCd: pkgUnit,
          qtyUnitCd: qtyUnit,
        });
        if (!cancelled) setSequence(String(next));
      } catch {
        // Best-effort convenience only — leave the field blank on failure,
        // the user can still type a sequence manually.
      } finally {
        if (!cancelled) setIsSuggestingSequence(false);
      }
    };
    suggestSequence();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    sequenceTouched,
    outletId,
    origin,
    productType,
    pkgUnit,
    qtyUnit,
  ]);

  const preview = useMemo(() => {
    if (!origin || !productType || !pkgUnit || !qtyUnit || !sequence) {
      return null;
    }
    try {
      return {
        code: buildItemCd({
          originNationCd: origin,
          productTypeCd: productType,
          pkgUnitCd: pkgUnit,
          qtyUnitCd: qtyUnit,
          sequence: Number(sequence),
        }),
        error: null as string | null,
      };
    } catch (err) {
      return {
        code: null as string | null,
        error: err instanceof Error ? err.message : 'Invalid combination',
      };
    }
  }, [origin, productType, pkgUnit, qtyUnit, sequence]);

  const handleConfirm = () => {
    if (preview?.code) onConfirm(preview.code);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Build Item Code" size="md">
      <div className="space-y-4 text-sm text-gray-900">
        {itemName && (
          <p className="text-xs text-gray-500">
            Building the RRA item code for <strong>{itemName}</strong>.
          </p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Origin Nation Code"
            value={origin}
            onChange={(value) => setOrigin(value.toUpperCase().slice(0, 2))}
            maxLength={2}
            helperText="RW for Rwanda. Only change this if configuring a non-Rwanda fiscal integration."
          />
          <DropdownSelect
            label="Item Type"
            value={productType}
            onChange={setProductType}
            options={EBM_PRODUCT_TYPE_OPTIONS.map((o) => ({ ...o }))}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <DropdownSelect
            label="Packaging Unit"
            value={pkgUnit}
            onChange={setPkgUnit}
            options={PACKAGING_OPTIONS}
            placeholder="Select packaging unit"
            searchable
          />
          <DropdownSelect
            label="Quantity Unit"
            value={qtyUnit}
            onChange={setQtyUnit}
            options={QUANTITY_OPTIONS}
            placeholder="Select quantity unit"
            searchable
          />
        </div>

        <Input
          label="Sequence"
          type="number"
          min={1}
          max={9999999}
          value={sequence}
          onChange={(value) => {
            setSequence(value);
            setSequenceTouched(true);
          }}
          helperText={
            isSuggestingSequence
              ? 'Suggesting the next available sequence…'
              : 'Auto-suggested from this outlet’s existing items with the same fields — change it if needed.'
          }
        />

        <div className="rounded-xl border border-gray-200 bg-app-bg p-4">
          <p className="mb-2 text-xs font-medium text-gray-600">
            Item Code Preview
          </p>
          {preview?.code ? (
            <p className="font-mono text-base tracking-wide text-gray-900">
              <span className="rounded bg-blue-50 px-1 text-blue-700">
                {origin}
              </span>
              <span className="rounded bg-purple-50 px-1 text-purple-700">
                {productType}
              </span>
              <span className="rounded bg-amber-50 px-1 text-amber-700">
                {pkgUnit}
              </span>
              <span className="rounded bg-teal-50 px-1 text-teal-700">
                {qtyUnit}
              </span>
              <span className="rounded bg-gray-100 px-1 text-gray-700">
                {String(sequence).padStart(7, '0')}
              </span>
            </p>
          ) : (
            <p className="text-sm text-gray-400">
              Select all fields above to preview the code.
            </p>
          )}
          {preview?.error && (
            <p className="mt-2 text-xs text-red-600">{preview.error}</p>
          )}
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <p>
            Only 2-letter packaging/quantity codes are offered here. RRA&apos;s
            official tables include some 3-letter and 1-letter codes (e.g.
            Litre, Kilometre, Container) that the current EBM integration
            can&apos;t yet accept — pick the closest 2-letter equivalent for now
            and flag it to an administrator if it doesn&apos;t fit.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!preview?.code}>
            Use this code
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ItemCodeBuilderModal;
